import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { SelectLayout } from 'components/selects/SelectLayout';
import { ITranslationBank } from 'types/agentQA';
import { AgentQAModel } from 'models/AgentQAModel';

interface IProps {
  agentType: string;
  selectedBanks: ITranslationBank[];
  onChange: (banks: ITranslationBank[]) => void;
  model: AgentQAModel;
}

const BankSelector: React.FC<IProps> = ({ agentType, selectedBanks, onChange, model }) => {
  const [availableBanks, setAvailableBanks] = useState<ITranslationBank[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!agentType) {
      setAvailableBanks([]);
      return;
    }

    const fetchBanks = async () => {
      setLoading(true);
      setError(null);
      try {
        const banks = await model.fetchBanksByAgentType(agentType);
        setAvailableBanks(banks || []);
      } catch (err) {
        console.error('Failed to fetch banks:', err);
        setError('Failed to load banks');
      } finally {
        setLoading(false);
      }
    };

    fetchBanks();
  }, [agentType, model]);

  const handleChange = (value: ITranslationBank[] | null) => {
    onChange(value ?? []);
  };

  return (
    <Section>
      <SectionTitle>Translation Banks *</SectionTitle>
      {loading ? (
        <LoadingText>Loading banks...</LoadingText>
      ) : error ? (
        <ErrorText>{error}</ErrorText>
      ) : (
        <SelectLayout
          data={availableBanks}
          onChange={handleChange}
          label="Select translation banks"
          optionLabelKey="name"
          optionValueKey="id"
          currentValue={selectedBanks}
          multiple
        />
      )}
      <HelpText>Select one or more translation banks to test with this agent.</HelpText>
    </Section>
  );
};

export default BankSelector;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 10px;
  font-size: 16px;
  color: #333;
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: #666;
  padding: 10px;
  font-style: italic;
`;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 14px;
  padding: 10px;
  background-color: #ffebee;
  border-radius: 4px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 8px;
  font-style: italic;
`;
